import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, CalendarDays, ArrowLeft, Share2 } from "lucide-react";
import { ARTICLES, SITE_URL, readingTime, formatDate } from "@/data/articles";
import { ArticleCard } from "@/components/ArticleCard";
import { BlogHeader } from "@/components/BlogHeader";
import { Footer } from "@/components/Footer";
import { scrollToId } from "@/lib/scroll";

export default function Articolo() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const article = ARTICLES.find((a) => a.slug === slug);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (article) {
            document.title = `${article.title} | Riflessioni — Dott. Giovanni De Lorenzo`;
            const meta = document.querySelector('meta[name="description"]');
            if (meta) meta.setAttribute("content", article.description);
        }
    }, [article]);

    if (!article) {
        return (
            <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-6 text-center" data-testid="article-not-found">
                <h1 className="font-serif text-4xl text-ink">Articolo non trovato</h1>
                <Link to="/riflessioni" data-testid="article-back-link" className="mt-6 inline-flex items-center gap-2 text-petrol font-semibold">
                    <ArrowLeft size={16} /> Torna alle Riflessioni
                </Link>
            </div>
        );
    }

    const url = `${SITE_URL}/riflessioni/${article.slug}`;
    const shareText = encodeURIComponent(`${article.title} — ${url}`);
    const shareFb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    const related = ARTICLES.filter((a) => a.slug !== slug && a.category === article.category).slice(0, 3);

    const bookColloquio = () => {
        navigate("/");
        setTimeout(() => scrollToId("#contatti"), 350);
    };

    return (
        <div className="min-h-screen bg-cream" data-testid="articolo-page">
            <BlogHeader />
            <main>
                <article className="max-w-5xl mx-auto px-6 sm:px-12 py-14 md:py-20">
                    <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-14 items-start">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                            <Link to="/riflessioni" data-testid="article-back-to-list" className="inline-flex items-center gap-2 text-sm font-semibold text-petrol hover:text-petrol-soft transition-colors duration-300">
                                <ArrowLeft size={15} /> Tutte le riflessioni
                            </Link>
                            <div className="mt-8 flex flex-wrap items-center gap-3 text-xs sm:text-sm text-[#7A8C8E]">
                                <span className="rounded-full bg-petrol-light text-petrol font-semibold px-4 py-1.5" data-testid="article-category">
                                    {article.category}
                                </span>
                                <span className="flex items-center gap-1.5"><CalendarDays size={14} /> {formatDate(article.date)}</span>
                                <span className="flex items-center gap-1.5"><Clock size={14} /> {readingTime(article)} min di lettura</span>
                            </div>
                            <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink" data-testid="article-title">
                                {article.title}
                            </h1>
                        </motion.div>

                        {article.image && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.98 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, delay: 0.15 }}
                                className="overflow-hidden rounded-3xl shadow-xl shadow-petrol/10 w-full max-w-xs mx-auto lg:mx-0 lg:justify-self-end"
                            >
                                <img src={article.image} alt={article.imageAlt} className="w-full h-auto" data-testid="article-image" />
                            </motion.div>
                        )}
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.25 }}
                        className="mt-12 space-y-12 max-w-3xl"
                        data-testid="article-body"
                    >
                        {article.sections.map((s, i) => (
                            <section key={i}>
                                {s.subtitle && <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-ink">{s.subtitle}</h2>}
                                {s.paragraphs.map((p, j) => (
                                    <p key={j} className="mt-4 text-base sm:text-lg text-[#526063] leading-relaxed">{p}</p>
                                ))}
                            </section>
                        ))}

                        <blockquote className="rounded-3xl bg-sage-light border-l-4 border-sage-dark p-8 sm:p-10" data-testid="article-quote">
                            <p className="font-serif italic text-xl sm:text-2xl text-sage-dark leading-relaxed">
                                "{article.quote.text}"
                            </p>
                            <footer className="mt-4 text-sm font-semibold text-sage-dark/70">— {article.quote.author}</footer>
                        </blockquote>

                        {article.bookPromo && (
                            <div className="rounded-3xl bg-petrol text-cream p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="article-book-promo">
                                <p className="font-serif text-lg sm:text-xl leading-relaxed text-center sm:text-left">
                                    Il libro «Prima di pensare. Perché facciamo cose che non volevamo fare» è ora disponibile (Armando Editore)
                                </p>
                                <a
                                    href="https://www.armandoeditore.it/catalogo/prima-di-pensare-perche-facciamo-cose-che-non-volevamo-fare/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="article-book-promo-btn"
                                    className="shrink-0 inline-flex items-center rounded-full bg-cream text-petrol px-7 py-3.5 text-sm font-semibold hover:bg-white hover:shadow-lg transition-all duration-300"
                                >
                                    Scopri il libro
                                </a>
                            </div>
                        )}
                    </motion.div>

                    <div className="mt-14 rounded-3xl bg-white border border-ink/5 shadow-sm p-8 flex flex-col sm:flex-row items-center gap-6" data-testid="article-author-box">
                        <img
                            src="/images/foto2.webp"
                            alt="Il Dott. Giovanni De Lorenzo"
                            className="h-24 w-24 rounded-full object-cover border-4 border-sage-light shrink-0"
                            loading="lazy"
                        />
                        <div className="text-center sm:text-left">
                            <p className="text-xs uppercase tracking-[0.2em] text-sage font-semibold">Scritto da</p>
                            <p className="mt-1 font-serif text-xl font-semibold text-ink">Dott. Giovanni De Lorenzo</p>
                            <p className="mt-1 text-sm text-[#526063]">Psicologo · Psicoterapeuta cognitivo-comportamentale · Ipnologo clinico ericksoniano</p>
                        </div>
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="article-actions">
                        <button
                            data-testid="article-book-btn"
                            onClick={bookColloquio}
                            className="inline-flex items-center rounded-full bg-petrol text-cream px-8 py-4 text-base font-semibold hover:bg-petrol-soft hover:shadow-lg transition-all duration-300"
                        >
                            Prenota un colloquio
                        </button>
                        <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-ink/50 font-semibold">
                                <Share2 size={14} /> Condividi
                            </span>
                            <a
                                href={`https://wa.me/?text=${shareText}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Condividi su WhatsApp"
                                data-testid="article-share-whatsapp"
                                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] text-white hover:scale-110 transition-transform duration-300"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                            </a>
                            <a
                                href={shareFb}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Condividi su Facebook"
                                data-testid="article-share-facebook"
                                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-110 transition-transform duration-300"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    <p className="mt-12 pt-8 border-t border-ink/10 text-xs text-[#7A8C8E] italic leading-relaxed text-center" data-testid="article-disclaimer">
                        Questo articolo ha scopo divulgativo e non sostituisce una consulenza professionale.
                    </p>
                </article>

                {related.length > 0 && (
                    <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-20" data-testid="article-related">
                        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-ink">Articoli correlati</h2>
                        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {related.map((a) => (
                                <ArticleCard key={a.slug} article={a} testid={`related-card-${a.slug}`} />
                            ))}
                        </div>
                    </section>
                )}
            </main>
            <Footer />
        </div>
    );
}
